import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { SelectFormFieldComponent } from 'app/shared/components/forms/fields/select-form-field/select-form-field.component';
import { ProfileInformationComponent } from 'app/shared/components/profile-information/profile-information.component';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HelpModalComponent } from '../../help-modal/help-modal.component';
import { HelpPageService } from 'app/help/help-page.service';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { IUser } from 'app/shared/models/users.model';
import { UsersService } from 'app/shared/services/users.service';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { SelectFormEnum } from 'app/shared/enums/select-form.enum';

@Component({
  selector: 'app-team-management-form',
  standalone: true,
  imports: [TranslateModule, SelectFormFieldComponent, CaseFormSectionContainerComponent, CommonModule, ProfileInformationComponent],
  templateUrl: './team-management-form.component.html',
  styleUrl: './team-management-form.component.scss'
})
export class TeamManagementFormComponent implements OnInit, OnDestroy {
  @Input() isAdmin: boolean = false;
  @ViewChild('addMemberBody', { static: true }) addMemberBody!: TemplateRef<any>;

  form: FormGroup;
  selectedUsers: IUser[] = [];
  selectedDropdownUsers: IUser[] = [];
  availableUsers: IUser[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private helpPageService: HelpPageService,
    private usersService: UsersService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      teamDropdown: [null],
    });
  }

  ngOnInit(): void {
    this.loadManagementTeam();
  }

  loadManagementTeam(): void {
    this.helpPageService
      .getManagementTeam(0, 10)
      .pipe(takeUntil(this.destroy$))
      .subscribe((teamMembers) => {
        this.selectedUsers = teamMembers;
      });
  }


  updateAvailableUsers(): void {
    this.helpPageService
      .getManagementTeam(0, 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allUsers) => {
        this.availableUsers = allUsers.filter(
          (user) => !this.selectedUsers.some((selected) => selected.id === user.id)
        );
      });
  }


  onSelectTeamMember(user: any): void {
    this.selectedDropdownUsers = [];
    this.selectedDropdownUsers.push(user);
    this.form.get('teamDropdown')?.setValue(user);
  }

  removeUser(index: number): void {
    const removedUser = this.selectedUsers[index];
    const removedUserId = removedUser.id;

    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal',
    });

    modalRef.componentInstance.title = this.translate.instant('label.delete_user');
    modalRef.componentInstance.message = this.translate.instant('messages.delete_user_confirmation');
    modalRef.componentInstance.notConfirmActionLabel = this.translate.instant('label.cancel');
    modalRef.componentInstance.confirmActionLabel = this.translate.instant('label.delete');

    modalRef.result.then(
      (result) => {
        if (result) {
          this.helpPageService
            .removeManagementTeamMember(removedUserId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              const successMessage = this.translate.instant('messages.success_remove_team_member');
              this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
              this.selectedUsers.splice(index, 1);
              this.availableUsers.push(removedUser);
              this.availableUsers = [...this.availableUsers];
            });
        }
      },
      () => {}
    );
  }

  searchAvailableUsers(event: string ,term: string, page: number = 0, take: number): void {
    this.usersService
      .getUsers(term, page * take, take)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        users = users.filter(
          (user: IUser) => !this.selectedUsers.some((selected) => selected.id === user.id)
        );
        if (event === SelectFormEnum.SCROLL){
          this.availableUsers = [...this.availableUsers, ...users]
        } else if (event === SelectFormEnum.SEARCH) {
          this.availableUsers = [...users];
        }
      });
  }

  openAddMemberModal(): void {
    this.searchAvailableUsers('search','', 0, 50);
    const modalRef = this.modalService.open(HelpModalComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'help-modal',
    });

    modalRef.componentInstance.title = this.translate.instant('buttons.add_new_member');
    modalRef.componentInstance.cancelButtonText = this.translate.instant('label.cancel');
    modalRef.componentInstance.saveButtonText = this.translate.instant('label.add');
    modalRef.componentInstance.modalBody = this.addMemberBody;

    modalRef.result.then(
      (result) => {
        if (result) {
          this.selectedDropdownUsers.forEach((user) => this.addTeamMember(user));
          this.selectedDropdownUsers = [];
        } else {
          this.selectedDropdownUsers = [];
        }
      },
      () => {
        this.selectedDropdownUsers = [];
      }
    );
  }

  addTeamMember(user: any): void {
    this.helpPageService
      .addManagementTeamMember(user.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedUsers.push(user);
        this.availableUsers = this.availableUsers.filter((u) => u.id !== user.id);
        const successMessage = this.translate.instant('messages.success_team_member');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

